import mickeLogo from '@/assets/brands/mick-e.png';
import mijninstallatiepartnerLogo from '@/assets/brands/mijninstallatiepartner.png';
import opchargeLogo from '@/assets/brands/opcharge.svg';
import stroompuntLogo from '@/assets/brands/stroompunt.png';
import vdhoLogo from '@/assets/brands/vdho.png';
import type { AccessLevel } from '@/auth/iam';

// ---------------------------------------------------------------------------
// White-labelling: which logo the app shell shows.
//
// One rule, derived entirely from the caller's IAM scope. The scope comes from
// Evinity and is fixed for the session — nothing here is user-selectable:
//
//     logo = brand for the caller's tenant, else the NRG logo
//
// - **domain** — no tenant of their own, so always the NRG logo.
// - **tenant** — their own tenant's logo.
// - **organisation** — the parent tenant's logo. `resolveAccessLevel` carries
//   `tenantId`/`tenantName` through for organisation-level callers whenever IAM
//   reports a parent, so this needs no extra lookup.
// - **anything unmapped** (organisation without a tenant, a tenant with no logo
//   here yet, IAM unavailable) falls back to the NRG logo.
//
// The logos are bundled assets keyed by **tenant name**, not by IAM id: the ids
// differ between tst/acc/prd, the names don't, so one entry covers every
// environment. Ids are still accepted (`tenantIds`) and win when present, for a
// tenant whose IAM name is ambiguous or about to change.

/** A client's visual identity in the shell. Today that is only a logo. */
export interface Brand {
  /** Stable key — matches the asset's filename stem. Used in tests and logs. */
  id: string;
  /** Display name, used as the logo's alt text. */
  name: string;
  /** Bundled asset URL (`import logo from '@/assets/brands/<id>.svg'`). */
  logoUrl: string;
  /**
   * IAM tenant names this brand covers, compared case-, accent- and
   * punctuation-insensitively (`Acme Energy B.V.` matches `acme energy bv`).
   * List every spelling that occurs across environments.
   */
  tenantNames: readonly string[];
  /** Exact IAM tenant ids. Checked before the names when set. */
  tenantIds?: readonly string[];
}

/**
 * The white-label catalogue.
 *
 * To add a client: drop `<id>.svg` into `src/assets/brands/`, import it here,
 * and add one entry. An empty catalogue is a valid state — every caller then
 * sees the NRG logo.
 */
export const BRANDS: readonly Brand[] = [
  { id: 'mick-e', name: 'Mick-e', logoUrl: mickeLogo, tenantNames: ['Mick-e'] },
  { id: 'opcharge', name: 'Opcharge', logoUrl: opchargeLogo, tenantNames: ['Opcharge'] },
  { id: 'stroompunt', name: 'Stroompunt', logoUrl: stroompuntLogo, tenantNames: ['Stroompunt'] },
  { id: 'vdho', name: 'VDHO.eu', logoUrl: vdhoLogo, tenantNames: ['VDHO.eu'] },
  {
    id: 'mijninstallatiepartner',
    name: 'mijninstallatiepartner',
    logoUrl: mijninstallatiepartnerLogo,
    tenantNames: ['mijninstallatiepartner'],
  },
];

/**
 * Fold a tenant name down to what two spellings of the same client have in
 * common: lower case, no accents, no punctuation or spacing. Deliberately
 * lossy — `NRG Group` and `nrg-group` must collide.
 */
export function normalizeTenantName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/** The tenant whose branding a caller inherits, or `undefined` for domain callers. */
function brandingTenant(
  access: AccessLevel,
): { id: string | undefined; name: string | undefined } | undefined {
  switch (access.level) {
    case 'domain':
      return undefined;
    case 'tenant':
      return { id: access.tenantId, name: access.tenantName };
    case 'organisation':
      // Only branded when IAM reports a parent tenant; a standalone
      // organisation has nothing to inherit and keeps the fallback.
      return (access.tenantId ?? access.tenantName)
        ? { id: access.tenantId, name: access.tenantName }
        : undefined;
  }
}

/**
 * The brand for a caller, or `undefined` when they get the NRG fallback.
 *
 * `catalog` is injectable so the tests don't depend on which clients happen to
 * be onboarded; production callers pass nothing.
 */
export function resolveBrand(
  access: AccessLevel | undefined,
  catalog: readonly Brand[] = BRANDS,
): Brand | undefined {
  if (!access) return undefined;
  const tenant = brandingTenant(access);
  if (!tenant) return undefined;

  if (tenant.id) {
    const byId = catalog.find((brand) => brand.tenantIds?.includes(tenant.id as string));
    if (byId) return byId;
  }
  if (tenant.name) {
    const normalized = normalizeTenantName(tenant.name);
    // An entry whose name normalizes to nothing (e.g. `'---'`) would match a
    // caller with an equally empty name — guard rather than mis-brand.
    if (normalized) {
      return catalog.find((brand) =>
        brand.tenantNames.some((candidate) => normalizeTenantName(candidate) === normalized),
      );
    }
  }
  return undefined;
}
